import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { USAGE_COST_ENDPOINTS, fetchUsageCostData, fetchUsageCostMetadata } from './api';
import type {
  UsageBreakdownRow,
  UsageClinicRow,
  UsageCostFilters,
  UsageDataQuality,
  UsageMetadata,
  UsageSummary,
  UsageTimelineRow,
  UsageTrendGroup,
  UsageTrendMetric,
  UsageTrendView,
  UsageTrends,
  UsageUserRow,
} from './types';

export const USAGE_COST_FILTER_DEBOUNCE_MS = 300;

export const usageCostFilterKey = (filters: UsageCostFilters) =>
  [
    'usage-costs',
    filters.startDate,
    filters.endDate,
    filters.clinicId ?? null,
    filters.userId ?? null,
    filters.feature ?? null,
    filters.provider ?? null,
    filters.model ?? null,
  ] as const;

export const usageCostMetadataKey = (filters: Pick<UsageCostFilters, 'startDate' | 'endDate'>) =>
  ['usage-costs', 'metadata', filters.startDate, filters.endDate] as const;

export const useDebouncedUsageCostFilters = (filters: UsageCostFilters) => {
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedFilters(filters);
    }, USAGE_COST_FILTER_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [filters]);

  return debouncedFilters;
};

export const useUsageCostQueries = (
  filters: UsageCostFilters,
  trendGroup: UsageTrendView,
  trendMetric: UsageTrendMetric
) => {
  const debouncedFilters = useDebouncedUsageCostFilters(filters);
  const filterKey = useMemo(() => usageCostFilterKey(debouncedFilters), [debouncedFilters]);
  const metadataKey = useMemo(() => usageCostMetadataKey(debouncedFilters), [debouncedFilters]);
  const groupedTrend = trendGroup === 'user' ? 'user' : 'clinic';

  const summaryQuery = useQuery({
    queryKey: [...filterKey, 'summary'],
    queryFn: ({ signal }) =>
      fetchUsageCostData<UsageSummary>(USAGE_COST_ENDPOINTS.summary, debouncedFilters, {}, { signal }),
  });

  const timelineQuery = useQuery({
    queryKey: [...filterKey, 'timeline'],
    queryFn: ({ signal }) =>
      fetchUsageCostData<UsageTimelineRow[]>(USAGE_COST_ENDPOINTS.timeline, debouncedFilters, {}, { signal }),
  });

  const featureBreakdownQuery = useQuery({
    queryKey: [...filterKey, 'breakdown', 'feature'],
    queryFn: ({ signal }) =>
      fetchUsageCostData<UsageBreakdownRow[]>(
        USAGE_COST_ENDPOINTS.breakdown,
        debouncedFilters,
        { by: 'feature' },
        { signal }
      ),
  });

  const modelBreakdownQuery = useQuery({
    queryKey: [...filterKey, 'breakdown', 'model'],
    queryFn: ({ signal }) =>
      fetchUsageCostData<UsageBreakdownRow[]>(
        USAGE_COST_ENDPOINTS.breakdown,
        debouncedFilters,
        { by: 'model' },
        { signal }
      ),
  });

  const clinicsQuery = useQuery({
    queryKey: [...filterKey, 'clinics'],
    queryFn: ({ signal }) =>
      fetchUsageCostData<UsageClinicRow[]>(USAGE_COST_ENDPOINTS.clinics, debouncedFilters, { limit: 100 }, { signal }),
  });

  const usersQuery = useQuery({
    queryKey: [...filterKey, 'users'],
    queryFn: ({ signal }) =>
      fetchUsageCostData<UsageUserRow[]>(USAGE_COST_ENDPOINTS.users, debouncedFilters, { limit: 100 }, { signal }),
  });

  const dataQualityQuery = useQuery({
    queryKey: [...filterKey, 'data-quality'],
    queryFn: ({ signal }) =>
      fetchUsageCostData<UsageDataQuality>(USAGE_COST_ENDPOINTS.dataQuality, debouncedFilters, {}, { signal }),
  });

  const metadataQuery = useQuery({
    queryKey: metadataKey,
    queryFn: ({ signal }) => fetchUsageCostMetadata<UsageMetadata>(debouncedFilters, { signal }),
  });

  const trendsQuery = useQuery({
    queryKey: [...filterKey, 'trends', groupedTrend, trendMetric],
    queryFn: ({ signal }) =>
      fetchUsageCostData<UsageTrends>(
        USAGE_COST_ENDPOINTS.trends,
        debouncedFilters,
        {
          group_by: groupedTrend satisfies UsageTrendGroup,
          metric: trendMetric,
          limit: 8,
        },
        { signal }
      ),
    enabled: trendGroup !== 'overall',
  });

  return {
    summaryQuery,
    timelineQuery,
    featureBreakdownQuery,
    modelBreakdownQuery,
    clinicsQuery,
    usersQuery,
    dataQualityQuery,
    metadataQuery,
    trendsQuery,
  };
};
